import React, { createContext, useContext, useEffect, useState } from 'react'
import { authService, AuthSessionUser } from './authService'
import { UserRole, UserProfile, Membership } from '../../types'
import { supabase } from '../../lib/supabase'
import { hasOperation } from './permissions'

export interface AuthDataError {
  step: 'profile' | 'memberships' | 'session'
  message: string
  email?: string
}

interface AuthContextType {
  user: AuthSessionUser | null
  loading: boolean
  authDataError: AuthDataError | null
  retryAuthData: () => Promise<void>
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  hasRole: (role: UserRole, teamId?: string) => boolean
  hasCapability: (capability: string, teamId?: string) => boolean
  isMedic: (teamId?: string) => boolean
  isCoach: (teamId?: string) => boolean
  isAdmin: () => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthSessionUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [authDataError, setAuthDataError] = useState<AuthDataError | null>(null)

  const loadUserData = async (userId: string, email: string): Promise<boolean> => {
    let profile: UserProfile | undefined
    try {
      profile = await authService.getProfile(userId)
    } catch (err: unknown) {
      setAuthDataError({
        step: 'profile',
        message: err instanceof Error ? err.message : 'Error al consultar perfil de usuario en base de datos',
        email,
      })
      setUser({ id: userId, email, memberships: [] })
      return false
    }

    let memberships: Membership[] = []
    try {
      memberships = await authService.getMemberships(userId)
    } catch (err: unknown) {
      setAuthDataError({
        step: 'memberships',
        message: err instanceof Error ? err.message : 'Error al consultar membresías y roles en base de datos',
        email,
      })
      setUser({ id: userId, email, profile, memberships: [] })
      return false
    }

    setAuthDataError(null)
    setUser({
      id: userId,
      email,
      profile,
      memberships,
    })
    return true
  }

  const retryAuthData = async () => {
    if (!supabase) return
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) {
      setLoading(true)
      try {
        await loadUserData(session.user.id, session.user.email || '')
      } finally {
        setLoading(false)
      }
    } else {
      setAuthDataError(null)
      setUser(null)
    }
  }

  useEffect(() => {
    let isMounted = true

    async function initSession() {
      try {
        if (!supabase) return
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user && isMounted) {
          await loadUserData(session.user.id, session.user.email || '')
        }
      } catch (err: unknown) {
        if (isMounted) {
          setAuthDataError({
            step: 'session',
            message: err instanceof Error ? err.message : 'Error al recuperar la sesión activa',
          })
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    initSession()

    if (supabase) {
      const { data: authListener } = supabase.auth.onAuthStateChange(async (_event, session) => {
        if (!isMounted) return

        if (!session?.user) {
          setUser(null)
          setAuthDataError(null)
          return
        }

        await loadUserData(session.user.id, session.user.email || '')
      })

      return () => {
        isMounted = false
        authListener.subscription.unsubscribe()
      }
    } else {
      setLoading(false)
    }

    return () => {
      isMounted = false
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    // CRÍTICO: No llamamos a setLoading(true) aquí.
    // LoginView gestiona su propio estado isSubmitting en el formulario.
    // Si pusiéramos setLoading(true), AppContent desmontaría LoginView, destruyendo
    // cualquier mensaje de error devuelto por Supabase Auth (Invalid login credentials).
    try {
      const { user: authUser, error } = await authService.signIn(email, password)
      if (error) {
        return { error }
      }
      setAuthDataError(null)
      setUser(authUser)
      return { error: null }
    } catch (err: unknown) {
      return { error: err instanceof Error ? err : new Error(String(err)) }
    }
  }

  const signOut = async () => {
    setLoading(true)
    try {
      await authService.signOut()
      setUser(null)
      setAuthDataError(null)
    } finally {
      setLoading(false)
    }
  }

  const hasRole = (role: UserRole, teamId?: string): boolean => {
    if (!user) return false
    return user.memberships.some((m) => {
      if (!m.is_active) return false
      if (teamId && m.team_id && m.team_id !== teamId) return false
      return m.roles.includes(role)
    })
  }

  const hasCapability = (capability: string, teamId?: string): boolean => {
    if (!user) return false
    return user.memberships.some((m) => {
      if (!m.is_active) return false
      if (teamId && m.team_id && m.team_id !== teamId) return false
      return hasOperation(m.roles, m.capabilities || [], capability)
    })
  }

  const isMedic = (teamId?: string): boolean => hasRole('medic', teamId)

  // Identidad de entrenador principal. Las operaciones usan hasCapability.
  const isCoach = (teamId?: string): boolean => {
    if (!user) return false
    return user.memberships.some((m) => {
      if (!m.is_active) return false
      if (teamId && m.team_id && m.team_id !== teamId) return false
      return (
        m.roles.includes('coach')
      )
    })
  }

  const isAdmin = (): boolean => {
    if (!user) return false
    return Boolean(
      user.is_system_admin ||
      user.memberships.some((m) => m.is_active && (m.roles.includes('admin') || m.capabilities?.includes('manage_users')))
    )
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authDataError,
        retryAuthData,
        signIn,
        signOut,
        hasRole,
        hasCapability,
        isMedic,
        isCoach,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth debe utilizarse dentro de un AuthProvider')
  }
  return context
}
